import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import { Divider} from '@mui/material';
import InfoItemBox from './InfoItemBox';
import { InfoItemBoxProps } from '../dataObjects/contentTypes';

const content: InfoItemBoxProps[] =  [
    {
      title: "Commence Recordkeeping and Reporting",
      blocks: [
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              content: "Once your committee is established, you will report your campaign finance activity. Your reports will be submitted to your local election official. You will need to contact your local election official for your reporting schedule."
            }
          ]
        },
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              content: "You can use this form to file campaign finance activity: "
            },
            {
              type: "link",
              text: "CPF M 102: Municipal Campaign Finance Report",
              url: "CPF M 102"
            },
            {
              type: "text",
              content: " ("
            },
            {
              type: "link",
              text: "Editable version",
              url: "Editable version"
            },
            {
              type: "text",
              content: ") ("
            },
            {
              type: "link",
              text: "Instructions",
              url: "Instructions"
            },
            {
              type: "text",
              content: ")."
            }
          ]
        },
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              content: "Candidates and committees must maintain detailed accounts of all contributions received and all expenditures made. These records must be kept separate and distinct from any other accounts or records and must be preserved for 6 years from the relevant election."
            }
          ]
        }
      ]
    },
    {
      title: "Use OCPF Reporter 7 to Create and Print Campaign Finance Reports",
      blocks: [
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              content: "Local filers can use R7 (Reporter 7) to create, print, sign and file campaign finance reports. "
            },
            {
              type: "link",
              text: "Click here",
              url: "https://www.youtube.com/watch?v=49A1EW6iARg"
            },
            {
              type: "text",
              content: " for a short instructional video."
            }
          ]
        },
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              content: "Please click "
            },
            {
              type: "link",
              text: "here",
              url: "https://ocpfreporter.us/LocalFilerSignup"
            },
            {
              type: "text",
              content: " to register to use R7."
            }
          ]
        },
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              content: '"Local filers" include all town candidates, and all city candidates who don\'t e-file with OCPF.'
            }
          ]
        }
      ]
    }, 
    {
      title: "Independent expenditure PACS should also familiarize themselves with the following:",
      blocks: [
        {
          type: "accordion",
          title: "Independent Expenditure PACs",
          content: [
            {
              type: "list",
              listType: "number",
              items: [
                {
                  content: [
                    {
                      type: "text",
                      isBold: true,
                      content: "Independent Expenditure PACs"
                    },
                    {
                      type: "text",
                      content: ". A political action committee that receives contributions to make independent expenditures is an \"independent expenditure PAC.\" Independent expenditure PACs may receive contributions from individuals without limit, and from corporations and other entities that are otherwise prohibited from contributing to PACs pursuant to M.G.L. c. 55, § 8. Any entity that raises or solicits funds for the purpose of making a contribution to an independent expenditure PAC is an independent expenditure PAC subject to all requirements applicable to independent expenditure PACs."
                    }
                  ]
                }
              ]
            },
            {
              type: "list",
              listType: "bullet",
              items: [
                {
                  content: [
                    {
                      type: "text",
                      content: "The required content and timing of reports filed by independent expenditure PACs is defined in 970 CMR 2.17(5). Except as indicated in 970 CMR 2.17, independent expenditure PACs are subject to all other requirements that apply to other PACs."
                    }
                  ]
                },
                {
                  content: [
                    {
                      type: "text",
                      content: "An independent expenditure PAC makes all of its expenditures without cooperating or consulting with any political committee other than another independent expenditure PAC. If an independent expenditure PAC makes coordinated expenditures with a candidate or candidate's committee, the independent expenditure PAC becomes a traditional PAC subject to the limits on contributions that may be received or made by other PACs."
                    }
                  ]
                },
                {
                  content: [
                    {
                      type: "text",
                      content: "An independent expenditure PAC must include the words \"independent expenditure political action committee\" in its name."
                    }
                  ]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  content: "— OCPF Regulations 970 CMR 2.17(4).",
                  isBold: false
                }
              ]
            }
          ]
        }
      ]
    },
    {
      title: "Additional Help & Information",
      blocks: [
        {
          type: "list",
          listType: "bullet",
          items: [
            {
              content: [
                {
                  type: "text",
                  content: "Campaign Finance Guide: "
                },
                {
                  type: "link",
                  text: "Independent Expenditure PACs(PACs) and People's Committees",
                  url: "https://ocpf2.blob.core.windows.net/pdf/guides/iepacguide.pdf"
                }
              ]
            },
            {
              content: [
                {
                  type: "link",
                  text: "Contribution Limits Chart",
                  url: "/Legal/ConttributionLimits"
                },
                {
                  type: "text",
                  content: "."
                }
              ]
            },
            {
              content: [
                {
                  type: "text",
                  content: "Help Video: "
                },
                {
                  type: "link",
                  text: "How to file an IEPAC report using Reporter 7",
                  url: "https://www.youtube.com/watch?v=6khFRu7xexw&t="
                }
              ]
            },
            {
              content: [
                {
                  type: "text",
                  content: "Help Video: "
                },
                {
                  type: "link",
                  text: "How to file a year-end report (IEPAC) using Reporter 7",
                  url: "https://www.youtube.com/watch?v=obbL_zb8RuY&t="
                }
              ]
            },
            {
              content: [
                {
                  type: "text",
                  content: "Public Employees and Campaigns: A "
                },
                {
                  type: "link",
                  text: "Fact Sheet",
                  url: "https://ocpf2.blob.core.windows.net/pdf/guides/fs_pub_emp.pdf"
                },
                {
                  type: "text",
                  content: "."
                }
              ]
            }
          ]
        }
      ]
    }
  ];


const RegisterInfoIEPAC = (): JSX.Element => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });


  return (
      <div>
        <Typography
              component="h1"
              variant="h4"
              align={'left'}
              data-aos={'fade-up'}
              gutterBottom
              sx={{
                fontWeight: 700,
              }}
              paddingTop={4}>
              Set Up Your Committee with OCPF
            </Typography>

            <Divider />
         
         {content.map((content, index) => (
          <InfoItemBox key={index} {...content} />
        ))}
      </div>
       
   
        
  );
};

export default RegisterInfoIEPAC;
